import is from '@sindresorhus/is';
import type { FunctionComponent } from 'react';

import { SkeletonBase } from '~/ui/core/skeleton/Skeleton';

import { useJobItemDetails } from '../../api/use-job-item-details';
import { resolveWorkflowState } from '../../hooks/use-job-profile-workflow';
import { ArchivedNotification } from './ArchivedNotification';
import { AssignReviewerNotification } from './AssignReviewerNotification';
import { GeneratingNotification } from './GeneratingNotification';
import { PublisherNotification } from './PublisherNotification';
import { ReviewerNotification } from './ReviewerNotification';

type ItemType = 'job-level' | 'job-role';
export type NotificationBarProps = {
    readonly architectureId: string;
    readonly itemId: string;
    readonly itemType: ItemType;
    readonly onDeleteComplete?: () => void;
    readonly onRestoreComplete?: () => void;
    readonly version: string | undefined;
};

export const NotificationBar: FunctionComponent<NotificationBarProps> = ({
    architectureId,
    itemId,
    itemType,
    onDeleteComplete,
    onRestoreComplete,
    version,
}) => {
    const { data: item, error, isLoading } = useJobItemDetails({ architectureId, itemId, itemType, version });

    if (isLoading) {
        return <SkeletonBase style={{ width: '200px' }} />;
    }

    if (!is.nullOrUndefined(error) || is.nullOrUndefined(item?.parentId)) {
        return null;
    }

    switch (resolveWorkflowState(item)) {
        case 'archived':
            return (
                <ArchivedNotification
                    architectureId={architectureId}
                    item={item}
                    itemType={itemType}
                    onDeleteComplete={onDeleteComplete}
                    onRestoreComplete={onRestoreComplete}
                />
            );
        case 'generating':
            return <GeneratingNotification item={item} />;
        case 'assign-reviewer':
            return <AssignReviewerNotification architectureId={architectureId} item={item} itemType={itemType} />;
        case 'review':
            return <ReviewerNotification architectureId={architectureId} item={item} itemType={itemType} />;
        case 'publish':
            return <PublisherNotification item={item} />;
        case 'none':
            return null;
    }
};
